import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Gift, ArrowLeft, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { resolveImageUrl } from '@/lib/image';
import { withAffiliateTag } from '@/lib/affiliate';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function GiftDetailPage() {
  const { id } = useParams();
  const [gift, setGift] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGift();
  }, [id]);

  const fetchGift = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/gifts/${id}`);
      setGift(response.data);
    } catch (error) {
      console.error('Error fetching gift:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="page-transition py-12 px-6 md:px-12">
        <div className="container mx-auto max-w-4xl text-center py-20">
          <p className="text-muted-foreground" data-testid="gift-detail-loading">Loading...</p>
        </div>
      </div>
    );
  }

  if (!gift) {
    return (
      <div className="page-transition py-12 px-6 md:px-12">
        <div className="container mx-auto max-w-4xl text-center py-20">
          <p className="text-muted-foreground" data-testid="gift-detail-not-found">Gift not found</p>
        </div>
      </div>
    );
  }

  const backLink = `/gifts/${gift.category}`;

  return (
    <div className="page-transition py-12 px-6 md:px-12">
      <div className="container mx-auto max-w-6xl">
        <Link to={backLink}>
          <Button variant="ghost" className="mb-8" data-testid="gift-detail-back-button">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Gifts
          </Button>
        </Link>

        <div className="grid md:grid-cols-2 gap-12">
          {/* Image */}
          <div className="relative h-[500px] rounded-xl overflow-hidden bg-muted" data-testid="gift-detail-image">
            {gift.image_url ? (
              <img
                src={resolveImageUrl(gift.image_url)}
                alt={gift.title}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Gift className="h-24 w-24 text-muted-foreground" />
              </div>
            )}
          </div>

          {/* Content */}
          <div className="space-y-6">
            <div>
              <h1 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-primary mb-2" data-testid="gift-detail-title">
                {gift.title}
              </h1>
            </div>

            <div className="flex gap-2 flex-wrap">
              {gift.budget_tier && (
                <span className="inline-block bg-accent/10 text-accent text-sm font-semibold px-4 py-2 rounded-full" data-testid="gift-detail-budget">
                  {gift.budget_tier}
                </span>
              )}
              {gift.target_age_range && (
                <span className="inline-block bg-muted text-foreground text-sm font-semibold px-4 py-2 rounded-full" data-testid="gift-detail-age">
                  {gift.target_age_range}
                </span>
              )}
            </div>

            {gift.description && (
              <div>
                <h2 className="font-heading text-2xl font-normal text-foreground mb-2">About This Gift</h2>
                <p className="text-base text-muted-foreground leading-relaxed" data-testid="gift-detail-description">
                  {gift.description}
                </p>
              </div>
            )}

            {gift.why_thoughtful && (
              <div className="bg-secondary/50 p-6 rounded-xl">
                <h2 className="font-heading text-2xl font-normal text-foreground mb-2">Why It's Thoughtful</h2>
                <p className="text-base text-muted-foreground leading-relaxed" data-testid="gift-detail-why-thoughtful">
                  {gift.why_thoughtful}
                </p>
              </div>
            )}

            {gift.developmental_value && (
              <div className="bg-secondary/50 p-6 rounded-xl">
                <h2 className="font-heading text-2xl font-normal text-foreground mb-2">Developmental Value</h2>
                <p className="text-base text-muted-foreground leading-relaxed" data-testid="gift-detail-developmental">
                  {gift.developmental_value}
                </p>
              </div>
            )}

            {gift.best_timing && (
              <div>
                <h3 className="font-body text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-2">Best Time to Give</h3>
                <p className="text-base text-foreground" data-testid="gift-detail-timing">{gift.best_timing}</p>
              </div>
            )}

            {gift.occasion_tags && gift.occasion_tags.length > 0 && (
              <div>
                <h3 className="font-body text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-2">Perfect For</h3>
                <div className="flex flex-wrap gap-2" data-testid="gift-detail-occasions">
                  {gift.occasion_tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-block bg-muted text-foreground text-sm px-3 py-1 rounded-md"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {gift.buy_link && (
              <Button className="rounded-full w-full" size="lg" asChild data-testid="gift-detail-buy-button">
                <a href={withAffiliateTag(gift.buy_link)} target="_blank" rel="noopener noreferrer">
                  Find This Gift
                  <ExternalLink className="ml-2 h-4 w-4" />
                </a>
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}